// TES-1863 (v2.3 §5/§6) — "Mängel ohne Bearbeiter": ownerlose Queue der eigenen Fachbereich-Gruppen.
// Listet Mängel mit gesetzter Fachabteilung (assigned_group_id ∈ meine Gruppen) ohne Bearbeiter
// (assigned_to NULL). Self-Register ("Ich übernehme") ruft die RPC defect_self_assign (Mig 334) und
// invalidiert die Tasks-Query, sodass der Mangel aus der ownerlosen Liste verschwindet.
//
// Sichtbarkeitsgrenze ist die defects-RLS (group-Scope) — die Liste fällt NIE auf team_id zurück
// (useTasksQuery filtert assigned_to IS NULL + meine Gruppen via group_members). Hat der Nutzer keine
// Gruppen-Mängel (z.B. tester ohne Mitgliedschaft), wird die Card NICHT gerendert (kein leeres Rauschen).
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Inbox, UserCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useTasksQuery, tasksKeys, type UnifiedTask } from "@/hooks/useTasksQuery";
import { logger } from "@/lib/logger";

interface MaengelOhneBearbeiterCardProps {
  /** Optional: open the consolidated defect dialog (TES-1864) for a defect id. */
  onSelectDefect?: (defectId: string) => void;
}

export function MaengelOhneBearbeiterCard({ onSelectDefect }: MaengelOhneBearbeiterCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { tasks, isLoading } = useTasksQuery();

  const ownerless: UnifiedTask[] = tasks.filter((task) => task.type === "group_unassigned_defect");

  // Card verbergen, wenn keine ownerlosen Gruppen-Mängel (auch während des ersten Ladens nichts rendern).
  if (isLoading || ownerless.length === 0) return null;

  const handleSelfAssign = async (defectId: string) => {
    const { error } = await supabase.rpc("defect_self_assign", { _defect_id: defectId });
    if (error) {
      logger.error("defect_self_assign failed", error);
      toast.error(t("tasks.maengelOhneBearbeiter.assignError"));
      return;
    }
    toast.success(t("tasks.maengelOhneBearbeiter.assignSuccess"));
    queryClient.invalidateQueries({ queryKey: tasksKeys.all });
  };

  return (
    <Card data-testid="maengel-ohne-bearbeiter-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Inbox className="h-4 w-4 text-muted-foreground" />
          {t("tasks.maengelOhneBearbeiter.title")}
        </CardTitle>
        <Badge variant="secondary" data-testid="maengel-ohne-bearbeiter-count">
          {ownerless.length}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-2">
        <ul className="space-y-2">
          {ownerless.map((task) => (
            <li
              key={task.id}
              data-testid="maengel-ohne-bearbeiter-item"
              className="flex items-center justify-between gap-2 rounded-md border p-2"
            >
              <button
                type="button"
                className="truncate text-left text-sm hover:underline"
                onClick={() => onSelectDefect?.(task.id)}
                title={task.defectData?.title}
              >
                {task.defectData?.title}
              </button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleSelfAssign(task.id)}
                aria-label={t("tasks.maengelOhneBearbeiter.assign")}
              >
                <UserCheck className="mr-1 h-3.5 w-3.5" />
                {t("tasks.maengelOhneBearbeiter.assign")}
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
